import { forwardRef, useCallback, useImperativeHandle, useState } from 'react'
import { useSnapCarousel } from '@educator/components/ProgramSections/shared/useSnapCarousel'
import { ValuePromiseCard } from './ValuePromiseCard'
import type { PeekSide, ValuePromiseItem } from './types'

export interface ValuePromiseCarouselHandle {
  goToPrev: () => void
  goToNext: () => void
}

interface ValuePromiseCarouselProps {
  items: ValuePromiseItem[]
  sectionLabel: string
  viewMoreLabel: string
  closeLabel: string
}

// Px a card must be clipped by before it counts as peeking (ignores sub-pixel).
const PEEK_CLIP_THRESHOLD_PX = 4

/** Edge-aligned row driven by the header arrows; derives each card's cut side. */
export const ValuePromiseCarousel = forwardRef<
  ValuePromiseCarouselHandle,
  ValuePromiseCarouselProps
>(({ items, sectionLabel, viewMoreLabel, closeLabel }, ref) => {
  const [peekSides, setPeekSides] = useState<PeekSide[]>([])

  // Derive each card's cut side; skip the update when unchanged.
  const syncPeek = useCallback((root: HTMLDivElement) => {
    const rootRect = root.getBoundingClientRect()
    const next = Array.from(root.children).map((child): PeekSide => {
      const rect = child.getBoundingClientRect()
      const cutRight = rect.right - rootRect.right
      const cutLeft = rootRect.left - rect.left
      if (cutRight > PEEK_CLIP_THRESHOLD_PX && cutRight >= cutLeft)
        return 'right'
      if (cutLeft > PEEK_CLIP_THRESHOLD_PX) return 'left'
      return null
    })
    setPeekSides((prev) =>
      prev.length === next.length && prev.every((v, i) => v === next[i])
        ? prev
        : next,
    )
  }, [])

  const { scrollRef, scrollByCard } = useSnapCarousel({ sync: syncPeek })

  useImperativeHandle(ref, () => ({
    goToPrev: () => scrollByCard(-1),
    goToNext: () => scrollByCard(1),
  }))

  return (
    <div
      ref={scrollRef}
      role='region'
      aria-label={sectionLabel}
      className='flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 scroll-px-6 [scrollbar-width:none] md:px-25 md:scroll-px-25 [&::-webkit-scrollbar]:hidden'
    >
      {items.map((item, index) => (
        <ValuePromiseCard
          key={item.id}
          {...item}
          positionIndex={index}
          peekSide={peekSides[index] ?? null}
          viewMoreLabel={viewMoreLabel}
          closeLabel={closeLabel}
        />
      ))}
    </div>
  )
})

ValuePromiseCarousel.displayName = 'ValuePromiseCarousel'
